import { useSyncExternalStore } from 'react';

/**
 * Theme system — dark/light/system with localStorage persistence.
 *
 * Mode semantics:
 *   - `dark`   — force dark, ignore OS preference
 *   - `light`  — force light, ignore OS preference
 *   - `system` — follow `prefers-color-scheme`, react to OS changes live
 *
 * `applyTheme` is idempotent: safe to call on mount, on storage events, and
 * on media-query changes. A matching inline script in `index.html` writes
 * the resolved theme attribute before React hydrates to avoid a FOUC for
 * users whose preference doesn't match the dark default.
 */

export type ThemeMode = 'dark' | 'light' | 'system';
type ResolvedTheme = 'dark' | 'light';

const STORAGE_KEY = 'trenora.theme';
const VALID_MODES: readonly ThemeMode[] = ['dark', 'light', 'system'];

function isThemeMode(v: unknown): v is ThemeMode {
  return typeof v === 'string' && (VALID_MODES as readonly string[]).includes(v);
}

/** Read the persisted mode. Falls back to `system` for new users. */
export function getInitialTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeMode(stored)) return stored;
  } catch {
    // localStorage can throw in privacy modes / sandboxed iframes — fall through.
  }
  return 'system';
}

function resolveSystemPref(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? resolveSystemPref() : mode;
}

/**
 * Write the resolved theme to <html data-theme="..."> and sync the
 * <meta name="theme-color"> so the mobile browser chrome (status bar,
 * address bar) matches the surface beneath it.
 */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(mode);
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'light' ? '#fafafa' : '#0a0a0a');
  }
}

/** Persist + apply. Use from the settings toggle. */
export function setTheme(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Persistence is best-effort; the in-memory store still applies.
  }
  applyTheme(mode);
  // Notify subscribers (other tabs use the storage event automatically; same
  // tab needs an explicit nudge via a synthetic event).
  window.dispatchEvent(new Event('trenora:theme'));
}

/**
 * Subscribe to theme changes. Fires on:
 *   - `setTheme` calls in the same tab
 *   - localStorage writes from other tabs (storage event)
 *   - prefers-color-scheme OS-level changes (only matters when mode = system)
 */
function subscribe(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent): void => {
    if (e.key === STORAGE_KEY) onChange();
  };
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  const onMq = (): void => {
    // Re-apply only if the user is on `system` — otherwise OS change is
    // irrelevant but the subscriber still wants to know the resolved value.
    if (getInitialTheme() === 'system') applyTheme('system');
    onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('trenora:theme', onChange);
  mq.addEventListener('change', onMq);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('trenora:theme', onChange);
    mq.removeEventListener('change', onMq);
  };
}

/** React hook — returns the current persisted mode (dark/light/system). */
export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(
    subscribe,
    () => getInitialTheme(),
    () => 'system',
  );
}

/** React hook — returns the *resolved* theme (dark/light), useful for
 *  picking icons or feeding charts the right palette. */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(
    subscribe,
    () => resolveTheme(getInitialTheme()),
    () => 'dark',
  );
}
